// Vendored from orca-front/lib/analytics/portable/ (PR #503). Promote to @kromatic/ab npm package once 3+ sites use this.
/**
 * Portable experiment assignment.
 *
 * Pure logic for picking and persisting an A/B variant. No framework or
 * Node imports — uses only `window.localStorage`, `globalThis.crypto`, and
 * the GA4 cookie helpers from `./gaIds`.
 *
 * The bucketing function (`pickVariantForSeed`) is deterministic: given the
 * same `(experiment.name, seed)` pair it returns the same variant on every
 * call, which lets us re-assign without storage and still keep stickiness.
 *
 * `assignVariant` is the high-level entry point: read existing assignment
 * from localStorage, derive a fresh one when missing, persist it.
 */

import { getGaIdsFromBrowserCookies } from './gaIds';
import type { ExperimentConfig } from './types';

export const EXPERIMENT_ASSIGNMENTS_STORAGE_KEY = 'orca_ab_assignments';
export const EXPERIMENT_ANON_ID_STORAGE_KEY = 'orca_ab_anon_id';

type AssignmentsStore = Record<string, string>;

function safeParseAssignments(raw: string | null): AssignmentsStore {
    if (!raw) {
        return {};
    }

    try {
        const parsed: unknown = JSON.parse(raw);

        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
            return {};
        }

        return Object.entries(parsed as Record<string, unknown>).reduce<AssignmentsStore>((acc, [key, value]) => {
            if (typeof value === 'string') {
                acc[key] = value;
            }
            return acc;
        }, {});
    } catch {
        return {};
    }
}

function readAssignments(): AssignmentsStore {
    if (typeof window === 'undefined') {
        return {};
    }

    try {
        return safeParseAssignments(window.localStorage.getItem(EXPERIMENT_ASSIGNMENTS_STORAGE_KEY));
    } catch {
        return {};
    }
}

function writeAssignments(assignments: AssignmentsStore): void {
    if (typeof window === 'undefined') {
        return;
    }

    try {
        window.localStorage.setItem(EXPERIMENT_ASSIGNMENTS_STORAGE_KEY, JSON.stringify(assignments));
    } catch {
        // Best-effort: storage may be blocked (private browsing, quota).
    }
}

function createRandomId(): string {
    if (typeof globalThis !== 'undefined' && globalThis.crypto && typeof globalThis.crypto.randomUUID === 'function') {
        return globalThis.crypto.randomUUID();
    }

    return `anon_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

/**
 * FNV-1a-ish 32-bit hash. Stable across runtimes: same input → same output.
 * Used to bucket a `(experiment, seed)` pair into a variant index.
 */
function stableHash(input: string): number {
    let hash = 2166136261;

    for (let index = 0; index < input.length; index += 1) {
        hash ^= input.charCodeAt(index);
        hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
    }

    return hash >>> 0;
}

function isKnownVariant(experiment: ExperimentConfig, variant: string): boolean {
    return experiment.variants.some(knownVariant => knownVariant === variant);
}

/**
 * Returns the per-browser anonymous id, creating one on first call. Used
 * when no GA4 client id is available (consent declined, GA blocked, fresh
 * session before the GA snippet has run).
 *
 * Falls back to a fresh random id (non-persisted) when `localStorage` is
 * unavailable.
 */
export function getOrCreateAnonymousId(): string {
    if (typeof window === 'undefined') {
        return createRandomId();
    }

    try {
        const existing = window.localStorage.getItem(EXPERIMENT_ANON_ID_STORAGE_KEY);

        if (existing && existing.trim()) {
            return existing;
        }

        const created = createRandomId();
        window.localStorage.setItem(EXPERIMENT_ANON_ID_STORAGE_KEY, created);
        return created;
    } catch {
        return createRandomId();
    }
}

/**
 * Resolves the bucketing seed: explicit override > GA4 client id > anon id.
 *
 * Returns `undefined` only when called outside a browser AND no override is
 * provided — callers should treat that as "no seed, use control".
 */
export function getAssignmentSeed(seedOverride?: string): string | undefined {
    if (seedOverride && seedOverride.trim()) {
        return seedOverride.trim();
    }

    if (typeof window === 'undefined') {
        return undefined;
    }

    const { ga_client_id } = getGaIdsFromBrowserCookies();

    if (ga_client_id) {
        return ga_client_id;
    }

    return getOrCreateAnonymousId();
}

/**
 * Pure deterministic bucketing. Given the same `(experiment.name, seed)`
 * pair, returns the same variant on every call. Distribution across variants
 * is approximately uniform for random seeds.
 */
export function pickVariantForSeed(experiment: ExperimentConfig, seed: string): string {
    const normalizedSeed = `${experiment.name}:${seed}`;
    const variantIndex = stableHash(normalizedSeed) % experiment.variants.length;
    return experiment.variants[variantIndex];
}

/**
 * High-level entry point. Returns the sticky variant for this browser:
 *  1. Read existing assignment from localStorage; return it if still valid.
 *  2. Otherwise compute a fresh assignment via `pickVariantForSeed`,
 *     persist it, and return it.
 *  3. Falls back to `experiment.control_variant` when no seed is available
 *     (SSR with no override).
 *
 * Aliased as `assignVariant` in the public API surface.
 */
export function getOrAssignExperimentVariant(experiment: ExperimentConfig, seedOverride?: string): string {
    const seed = getAssignmentSeed(seedOverride);

    if (!seed) {
        return experiment.control_variant;
    }

    const assignments = readAssignments();
    const existingVariant = assignments[experiment.name];

    if (existingVariant && isKnownVariant(experiment, existingVariant)) {
        return existingVariant;
    }

    const variant = pickVariantForSeed(experiment, seed);
    writeAssignments({
        ...assignments,
        [experiment.name]: variant,
    });

    return variant;
}

/**
 * Public alias matching the documented `@kromatic/ab` surface
 * (`assignVariant(experiment, seed?)`).
 */
export const assignVariant = getOrAssignExperimentVariant;

/**
 * Alias for `getAssignmentSeed` exported under the more explicit name used
 * by the `@kromatic/ab` public surface. Returns the GA4 client id with an
 * anon-id fallback (vs. the strict GA-only `getAssignmentSeed` exported by
 * `./gaIds`).
 */
export const getAssignmentSeedWithFallback = getAssignmentSeed;
